type PlanBillingUsageHistoryBarProps = {
    periodLabel: string;
    sessions: number;
    /** null = unlimited cycle: the whole bar is within-plan, no overage split. */
    included: number | null;
    /** Largest session value across all bars, scaled to leave room for the value label. */
    scale: number;
};

export function PlanBillingUsageHistoryBar({
    periodLabel,
    sessions,
    included,
    scale,
}: PlanBillingUsageHistoryBarProps) {
    const widthPct = scale > 0 ? Math.min(100, (sessions / scale) * 100) : 0;
    const overage = included === null ? 0 : Math.max(0, sessions - included);
    const sagePct = sessions > 0 ? ((sessions - overage) / sessions) * 100 : 100;
    const orchidPct = sessions > 0 ? (overage / sessions) * 100 : 0;
    // A period with no sessions has no bar to hold its label, so it moves outside.
    const labelInsideBar = widthPct > 0;

    return (
        // Deliberately static: no hover, no tooltip, no click action. Still a real button,
        // not a div — Biome's a11y rules reject tabIndex on a non-interactive role, and a
        // screen reader that Tabs through controls, not just browse mode, needs a real
        // focus stop to reach the aria-label's count and cap, absent from the visible text.
        <button
            type="button"
            aria-label={
                included === null
                    ? `${periodLabel}: ${sessions} sessions`
                    : `${periodLabel}: ${sessions} of ${included} sessions`
            }
            className="relative flex h-8 w-full items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
            {/* z-10 lifts the bar above the grid lines (see card stacking comment). */}
            <div
                className="relative z-10 flex h-full min-w-px overflow-hidden rounded-md"
                style={{ width: `${widthPct}%` }}
            >
                {/* Sessions within plan — sage (chart-5). Truncate label if it exceeds the bar.
                        accent-foreground per Figma: ink on light sage, near-white on dark sage-700. */}
                <div
                    className="flex h-full min-w-0 items-center bg-chart-5 px-2"
                    style={{ width: `${sagePct}%` }}
                >
                    {labelInsideBar && (
                        <span className="truncate text-xs text-accent-foreground">
                            {periodLabel}
                        </span>
                    )}
                </div>
                {/* Sessions exceeding plan — orchid (chart-2). */}
                {overage > 0 && (
                    <div className="h-full bg-chart-2" style={{ width: `${orchidPct}%` }} />
                )}
            </div>
            <span
                className="absolute top-1/2 z-10 flex -translate-y-1/2 items-center gap-2 pl-2 text-xs"
                style={{ left: `${widthPct}%` }}
            >
                <span className="text-foreground">{sessions}</span>
                {!labelInsideBar && <span className="text-muted-foreground">{periodLabel}</span>}
            </span>
        </button>
    );
}
